import React, { useEffect, useState, useCallback } from 'react';
import { Eye, Clock, CheckCircle2, Ban, AlertCircle } from 'lucide-react';
import usePaymentLinkActions from './usePaymentLinkActions';
import './PaymentLinksHistory.css';

/* ─── Module-scope sub-components ─────────────────────────────── */

const STATUS_META = {
  ACTIVE:    { label: 'Active',    icon: Clock,        cls: 'sl-plh-status--active' },
  PAID:      { label: 'Paid',      icon: CheckCircle2, cls: 'sl-plh-status--paid' },
  EXPIRED:   { label: 'Expired',   icon: AlertCircle,  cls: 'sl-plh-status--expired' },
  CANCELLED: { label: 'Cancelled', icon: Ban,          cls: 'sl-plh-status--cancelled' },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.ACTIVE;
  const Icon = meta.icon;
  return (
    <span className={`sl-plh-status ${meta.cls}`}>
      <Icon size={12} />
      <span>{meta.label}</span>
    </span>
  );
}

const formatRelative = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const dt = new Date(iso);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function HistoryRow({ link }) {
  return (
    <li className="sl-plh-row">
      <StatusChip status={link.status} />
      <div className="sl-plh-meta">
        <span className="sl-plh-views">
          <Eye size={12} />
          {link.viewCount || 0}
        </span>
        <span className="sl-plh-dot">·</span>
        <span>Sent {formatRelative(link.createdAt)}</span>
        {link.emailedAt && (
          <>
            <span className="sl-plh-dot">·</span>
            <span>Emailed</span>
          </>
        )}
      </div>
    </li>
  );
}

/* ─── Main ──────────────────────────────────────────────────────── */

/**
 * Inline list of every PaymentLink sent for this order. Shows status
 * + view count + sent time. Hides itself entirely when there are no
 * links — no empty state header noise on Order Detail.
 *
 * Props:
 *   orderId         — string
 *   refreshKey      — bump to force re-fetch (parent passes a counter
 *                     that increments after the popover creates a new
 *                     link)
 */
export default function PaymentLinksHistory({ orderId, refreshKey = 0 }) {
  const { listLinksForOrder } = usePaymentLinkActions();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLinks = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    const list = await listLinksForOrder(orderId);
    setLinks(list);
    setLoading(false);
  }, [orderId, listLinksForOrder]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks, refreshKey]);

  if (loading) {
    return (
      <div className="sl-plh-card sl-plh-card--loading">
        <div className="sl-plh-skeleton" />
        <div className="sl-plh-skeleton" />
      </div>
    );
  }

  if (!links.length) return null;

  return (
    <section className="sl-plh-card">
      <header className="sl-plh-head">
        <h3 className="sl-plh-title">Payment links sent</h3>
        <span className="sl-plh-count">
          {links.length} {links.length === 1 ? 'link' : 'links'}
        </span>
      </header>
      <ul className="sl-plh-list">
        {links.map((link) => (
          <HistoryRow key={link.id} link={link} />
        ))}
      </ul>
    </section>
  );
}
